// One state phrase for every client. Optional projection facts stay absent when the backend has no source.
import type { Row } from '../protocol/views.ts'
import { backendName, counted, elapsed, project, tokens } from './format.ts'
import { sessionStatus, statusTable, statusWords, type Status, type Tone } from './status.ts'

/** Additive row projection agreed with core's sessions-rows line. */
export type RowFacts = Pick<Row, 'activity' | 'outcome' | 'agentsWorking' | 'tasks' | 'background' | 'waitingText'>

export interface SessionRowWords {
  phrase: string
  /** Each item can be dropped whole. The directory is always last. */
  metadata: string[]
  status: Status
  tone: Tone
  elapsed?: string
}
export interface SessionRowOptions { showBackend?: boolean }

/** Formats facts only: no transcript lookups, inferred outcomes, counts or synthetic turn starts. */
export function sessionRow(row: Row & RowFacts, now = Date.now(), { showBackend = true }: SessionRowOptions = {}): SessionRowWords {
  const metadata = [
    row.agentsWorking ? counted(row.agentsWorking, 'agent') : '',
    row.tasks && row.tasks.total > 0 ? `${row.tasks.done}/${row.tasks.total} tasks` : '',
    showBackend ? backendName[row.backend] : '', project(row.cwd),
  ].filter(Boolean)
  const input = { state: row.state, operation: row.operation, needsYou: row.needsYou, waitingFor: row.waitingFor, outcome: row.outcome?.state, step: row.activity?.phrase }
  const status = sessionStatus(input), parts = [statusWords(input)]
  let time: string | undefined
  if (status === 'needs-you' && row.waitingText) parts[0] += `${row.waitingFor ? ': ' : ' · '}${row.waitingText}`
  if (status === 'working') {
    const start = row.activity?.since ?? NaN
    time = Number.isFinite(start) && Number.isFinite(now) ? elapsed(now - start) : undefined
    if (time) parts.push(time)
  }
  if (row.outcome?.state === status) {
    if (row.outcome.reason) parts.push(row.outcome.reason)
    if (row.outcome.tokens !== undefined) parts.push(`${tokens(row.outcome.tokens)} tokens`)
  }
  if (status === 'ready' && row.background) parts.push(counted(row.background, 'background task'))
  return { phrase: parts.join(' · '), metadata, status, tone: statusTable[status].tone, ...(time === undefined ? {} : { elapsed: time }) }
}
